import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, TrendingUp } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ProfitabilityCalculatorPage/components/CalculationForm';
import ResultsOverview from './ProfitabilityCalculatorPage/components/ResultsOverview';
import BusinessMetricsChart from './ProfitabilityCalculatorPage/components/BusinessMetricsChart';
import QuickAdjustmentSidebar from './ProfitabilityCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './ProfitabilityCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './ProfitabilityCalculatorPage/components/TipsSidebar';
import InfoContent from './ProfitabilityCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры
  revenue: '1000000', // Выручка, руб.
  costOfGoods: '600000', // Себестоимость, руб.
  operatingExpenses: '200000', // Операционные расходы, руб.
  
  // Дополнительные параметры
  taxRate: '20', // Ставка налога на прибыль (%)
  investmentAmount: '2000000', // Сумма инвестиций/активов, руб.
  industryType: 'retail', // Тип отрасли
  companySize: 'small', // Размер компании
  period: 'year', // Период расчета ('month', 'quarter', 'year')
  
  // Детализация расходов
  includeDetailedCosts: false, // Флаг для включения детализации расходов
  materialCosts: '300000', // Расходы на материалы и комплектующие
  laborCosts: '150000', // Расходы на оплату труда
  rentCosts: '50000', // Аренда
  utilitiesCosts: '20000', // Коммунальные платежи
  marketingCosts: '40000', // Маркетинг и реклама
  logisticsCosts: '30000', // Логистика и транспорт
  adminCosts: '60000', // Административные расходы
  otherCosts: '10000', // Прочие расходы
  
  // Дополнительные финансовые показатели
  includeAdditionalMetrics: false, // Флаг для включения дополнительных метрик
  interestExpense: '10000', // Процентные расходы (по кредитам)
  depreciation: '50000', // Амортизация
  accountsReceivable: '200000', // Дебиторская задолженность
  accountsPayable: '150000', // Кредиторская задолженность
  inventory: '300000', // Запасы
};

// Определение типов отраслей с бенчмарками
export const industryTypes = [
  { id: 'retail', name: 'Розничная торговля', avgGrossMargin: 25, avgNetMargin: 5, avgROI: 15 },
  { id: 'wholesale', name: 'Оптовая торговля', avgGrossMargin: 20, avgNetMargin: 4, avgROI: 12 },
  { id: 'manufacturing', name: 'Производство', avgGrossMargin: 35, avgNetMargin: 8, avgROI: 18 },
  { id: 'services', name: 'Услуги', avgGrossMargin: 45, avgNetMargin: 12, avgROI: 25 },
  { id: 'construction', name: 'Строительство', avgGrossMargin: 30, avgNetMargin: 7, avgROI: 15 },
  { id: 'agriculture', name: 'Сельское хозяйство', avgGrossMargin: 28, avgNetMargin: 6, avgROI: 10 },
  { id: 'it', name: 'IT и разработка ПО', avgGrossMargin: 60, avgNetMargin: 20, avgROI: 40 },
  { id: 'foodservice', name: 'Общественное питание', avgGrossMargin: 40, avgNetMargin: 8, avgROI: 20 }
];

// Определение размеров компаний
export const companySizes = [
  { id: 'micro', name: 'Микробизнес (до 15 сотр.)' },
  { id: 'small', name: 'Малый бизнес (до 100 сотр.)' },
  { id: 'medium', name: 'Средний бизнес (до 250 сотр.)' },
  { id: 'large', name: 'Крупный бизнес (более 250 сотр.)' }
];

// Определение периодов анализа
export const analysisPeriods = [
  { id: 'month', name: 'Месяц' },
  { id: 'quarter', name: 'Квартал' },
  { id: 'half_year', name: 'Полугодие' },
  { id: 'year', name: 'Год' }
];

/**
 * ProfitabilityCalculatorPage Component - Калькулятор рентабельности бизнеса 
 * с различными финансовыми показателями и сравнительным анализом.
 */
const ProfitabilityCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  // Предполагаем, что инструмент "profitability-calculator" добавлен в tools.js
  const tool = useMemo(() => getToolById('profitability-calculator') || {
    id: 'profitability-calculator',
    name: 'Калькулятор рентабельности',
    metaTitle: 'Калькулятор рентабельности бизнеса | Gensio',
    metaDescription: 'Расчет показателей рентабельности: ROI, ROA, ROE, маржинальность, NPV и других финансовых метрик для бизнеса. Анализ эффективности инвестиций и операционной деятельности компании.',
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор рентабельности бизнеса | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет показателей рентабельности: ROI, ROA, ROE, маржинальность, NPV и других финансовых метрик для бизнеса. Анализ эффективности инвестиций и операционной деятельности компании.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [selectedChartMetrics, setSelectedChartMetrics] = useState(['grossProfitMargin', 'netProfitMargin', 'roi']);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных анализов из localStorage ---
  useEffect(() => {
    const savedAnalysesData = localStorage.getItem('savedProfitabilityAnalyses');
    if (savedAnalysesData) {
      try {
        setSavedAnalyses(JSON.parse(savedAnalysesData));
      } catch (e) {
        console.error("Error loading saved analyses:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'retail':
          newState.industryType = 'retail';
          break;
        case 'manufacturing':
          newState.industryType = 'manufacturing';
          break;
        case 'detailed':
          newState.includeDetailedCosts = true;
          break;
        case 'advanced':
          newState.includeAdditionalMetrics = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setHistoricalData([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение анализа в localStorage
  const saveAnalysis = (name) => {
    if (!results) return;

    const newSavedAnalysis = {
      id: Date.now().toString(),
      name: name || `Анализ рентабельности от ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      industryType: formData.industryType
    };

    const updatedSavedAnalyses = [...savedAnalyses, newSavedAnalysis];
    setSavedAnalyses(updatedSavedAnalyses);
    localStorage.setItem('savedProfitabilityAnalyses', JSON.stringify(updatedSavedAnalyses));

    return newSavedAnalysis;
  };

  // Удаление сохраненного анализа
  const deleteSavedAnalysis = (id) => {
    const updatedSavedAnalyses = savedAnalyses.filter(analysis => analysis.id !== id);
    setSavedAnalyses(updatedSavedAnalyses);
    localStorage.setItem('savedProfitabilityAnalyses', JSON.stringify(updatedSavedAnalyses));
  };

  // Загрузка сохраненного анализа
  const loadSavedAnalysis = (id) => {
    const analysisToLoad = savedAnalyses.find(analysis => analysis.id === id);
    if (analysisToLoad) {
      setFormData(analysisToLoad.formData);
      setResults(analysisToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор рентабельности бизнеса'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              historicalData={historicalData}
              setHistoricalData={setHistoricalData}
              industryTypes={industryTypes}
              companySizes={companySizes}
              analysisPeriods={analysisPeriods}
              saveAnalysis={saveAnalysis}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                  industryTypes={industryTypes}
                />
                <BusinessMetricsChart 
                  results={results}
                  historicalData={historicalData}
                  selectedMetrics={selectedChartMetrics}
                  setSelectedMetrics={setSelectedChartMetrics}
                  industryType={formData.industryType}
                  industryTypes={industryTypes}
                />
              </>
            )}

            {/* --- Информационный контент о рентабельности --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              industryTypes={industryTypes}
            />

            {/* --- Панель сравнения анализов --- */}
            <ComparisonSidebar 
              savedAnalyses={savedAnalyses}
              deleteSavedAnalysis={deleteSavedAnalysis}
              loadSavedAnalysis={loadSavedAnalysis}
              industryTypes={industryTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default ProfitabilityCalculatorPage;